import api from './api';
import auth from './auth';

/**
 * User Service
 * Handles all user-related API calls
 */

const userService = {
  /**
   * Send login request with email and password.
   * Backend may respond with an OTP challenge if the credentials are valid.
   * @param {Object} credentials - { email, password }
   * @returns {Promise} - { requiresOtp, message, user?, token? }
   */
  login: async (credentials) => {
    try {
      const response = await api.post('/users/login', {
        email: credentials.email,
        password: credentials.password,
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Register a new user account
   * @param {Object} userData - { fullName, email, phone, password, requestedRole }
   * @returns {Promise} - { message, user }
   */
  register: async (userData) => {
    try {
      const response = await api.post('/users/register', {
        fullName: userData.fullName,
        email: userData.email,
        phone: userData.phone,
        password: userData.password,
        requestedRole: userData.requestedRole || 'Reception',
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Request an OTP for login verification
   * @param {Object} payload - { email }
   * @returns {Promise} - { message }
   */
  requestOtp: async (payload) => {
    try {
      const response = await api.post('/users/request-otp', {
        email: payload.email,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Verify OTP and complete login with email, password, and OTP
   * @param {Object} payload - { email, password, otp }
   * @returns {Promise} - { token, user }
   */
  verifyOtp: async (payload) => {
    try {
      const response = await api.post('/users/verify-otp-login', {
        email: payload.email,
        password: payload.password,
        otp: payload.otp,
      });

      if (response.data?.user) {
        auth.saveAuth(response.data.token || 'logged-in', response.data.user);
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Legacy OTP verification endpoint (backwards compatible)
   * @param {Object} payload - { email, otp }
   * @returns {Promise} - { token, user }
   */
  verifyOtpLegacy: async (payload) => {
    try {
      const response = await api.post('/users/verify-otp', {
        email: payload.email,
        otp: payload.otp,
      });

      if (response.data?.user) {
        auth.saveAuth(response.data.token || 'logged-in', response.data.user);
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Request access to a specific page/permission
   * @param {Object} payload - { permission }
   * @returns {Promise} - { message }
   */
  requestAccess: async (payload) => {
    try {
      const response = await api.post('/users/request-access', {
        permission: payload.permission,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Approve or reject user access request (admin only)
   * @param {string} userId - User ID
   * @param {Object} payload - { permission, approved, role?, status? }
   * @returns {Promise} - { message, user }
   */
  approveAccess: async (userId, payload) => {
    try {
      const response = await api.put(`/users/approve-access/${userId}`, {
        permission: payload.permission,
        approved: payload.approved,
        role: payload.role,
        status: payload.status,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Logout user
   * Clears local storage
   */
  logout: () => {
    auth.clearAuth();
  },

  /**
   * Get all users
   * @returns {Promise<Array>} - List of users
   */
  getAllUsers: async () => {
    try {
      const response = await api.get('/users');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get user by ID
   * @param {string} id - User ID
   * @returns {Promise<Object>} - User object
   */
  getUserById: async (id) => {
    try {
      const response = await api.get(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get instructor context: assigned trades, current batch, and scoped students.
   * @param {string} id - Instructor user ID
   * @returns {Promise<Object>}
   */
  getInstructorContext: async (id) => {
    try {
      const response = await api.get(`/users/${id}/instructor-context`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Create new user
   * @param {Object} userData - User data including password
   * @returns {Promise<Object>} - Created user object (without password)
   */
  createUser: async (userData) => {
    try {
      const response = await api.post('/users', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update user
   * @param {string} id - User ID
   * @param {Object} userData - User data to update (password will be hashed if provided)
   * @returns {Promise<Object>} - Updated user object
   */
  updateUser: async (id, userData) => {
    try {
      const response = await api.put(`/users/${id}`, userData);
      
      // Update local storage if updating current user
      const currentUser = auth.getUser() || {};
      if (currentUser._id === id) {
        auth.saveAuth(auth.getToken() || 'logged-in', response.data);
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Delete user
   * @param {string} id - User ID
   * @returns {Promise<Object>} - Deletion confirmation message
   */
  deleteUser: async (id) => {
    try {
      const response = await api.delete(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get current user from localStorage
   * @returns {Object|null} - Current user object or null
   */
  getCurrentUser: () => auth.getUser(),

  getAuthToken: () => auth.getToken(),

  isAuthenticated: () => auth.isAuthenticated(),
};

export default userService;
